import { useMutation } from '@apollo/client';
import { useToast } from '@synoptic/ui-kit/toast/toast-provider.js';
import { gql } from '@/__generated__/index.ts';

const SetAuthorHighlightedMutation = gql(`
  mutation SetAuthorHighlighted($id: ID!, $isHighlighted: Boolean!) {
    setAuthorHighlighted(id: $id, isHighlighted: $isHighlighted) {
      id
      isHighlighted
    }
  }
`);

export const useHighlightAuthor = () => {
  const toast = useToast();

  const [mutate, { loading }] = useMutation(SetAuthorHighlightedMutation, {
    onError: () => {
      toast.error({
        title: 'Failed to highlight author',
      });
    },
    onCompleted: () => {
      toast.success({
        title: 'Author highlighted successfully',
      });
    },
  });

  const highlightAuthor = (authorId: string) => {
    return mutate({
      variables: { id: authorId, isHighlighted: true },
      optimisticResponse: {
        setAuthorHighlighted: {
          id: authorId,
          isHighlighted: true,
          __typename: 'User',
        },
      },
    });
  };

  return { highlightAuthor, loading };
};

export const useUnhighlightAuthor = () => {
  const toast = useToast();

  const [mutate, { loading }] = useMutation(SetAuthorHighlightedMutation, {
    onError: () => {
      toast.error({
        title: 'Failed to unhighlight author',
      });
    },
    onCompleted: () => {
      toast.success({
        title: 'Author unhighlighted successfully',
      });
    },
  });

  const unhighlightAuthor = (authorId: string) => {
    return mutate({
      variables: { id: authorId, isHighlighted: false },
      optimisticResponse: {
        setAuthorHighlighted: {
          id: authorId,
          isHighlighted: false,
          __typename: 'User',
        },
      },
    });
  };

  return { unhighlightAuthor, loading };
};

export const useToggleHighlight = () => {
  const { highlightAuthor, loading: highlightLoading } = useHighlightAuthor();
  const { unhighlightAuthor, loading: unhighlightLoading } =
    useUnhighlightAuthor();

  const toggleHighlight = (authorId: string, isHighlighted: boolean) => {
    if (isHighlighted) {
      return unhighlightAuthor(authorId);
    } else {
      return highlightAuthor(authorId);
    }
  };

  return { toggleHighlight, loading: highlightLoading || unhighlightLoading };
};
